import React from 'react';
import { Stepper, Step, StepLabel, Box, Typography, useTheme, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { formatDate } from '../../utils/functions';

const TrackStepper = ({ activeStep, orderOn, shippedAt, deliveredAt, tracking = [] }) => {
    const theme = useTheme();

    const steps = [
        {
            status: "Ordered",
            date: orderOn ? formatDate(orderOn) : "N/A",
        },
        {
            status: "Shipped",
            date: shippedAt ? formatDate(shippedAt) : "N/A",
        },
        {
            status: "Delivered",
            date: deliveredAt ? formatDate(deliveredAt) : "N/A",
        },
    ];

    const getStepIcon = (step, index) => {
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;
    
        let iconColor = theme.palette.text.disabled;
        if (isCompleted) {
            iconColor = theme.palette.success.main;
        } else if (isActive) {
            iconColor = theme.palette.primary.main;
        }
    
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    ...(isActive && {
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                            '0%': {
                                boxShadow: `0 0 0 0 ${theme.palette.primary.light}`,
                            },
                            '75%': {
                                boxShadow: `0 0 0 6px ${theme.palette.primary.light}00`,
                            },
                            '100%': {
                                boxShadow: `0 0 0 0 ${theme.palette.primary.light}00`,
                            },
                        },
                    }),
                }}
            >
                {isCompleted ? (
                    <CheckCircleIcon sx={{ color: iconColor, fontSize: 20 }} />
                ) : (
                    <CircleOutlinedIcon sx={{ color: iconColor, fontSize: 20 }} />
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((step, index) => (
                    <Step key={step.status}>
                        <StepLabel
                            StepIconComponent={() => getStepIcon(step, index)}
                            sx={{
                                '& .MuiStepLabel-label': {
                                    fontWeight: 'medium',
                                    color: index <= activeStep ? theme.palette.text.primary : theme.palette.text.secondary,
                                },
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                {step.status}
                            </Typography>
                            {step.date !== "N/A" && (
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                    {step.date}
                                </Typography>
                            )}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Tracking History */}
            {tracking && tracking.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Tracking Updates
                    </Typography>
                    {tracking.map((track, index) => (
                        <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                {track.location}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(track.timestamp)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default TrackStepper;
